import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  selectAllNodes,
  selectAllPoints,
  selectFilteredTsps,
  selectInterconnects,
  selectFilteredInterconnects,
} from '../map/slice/mapSelectors';
import { useSelector } from 'react-redux';
import { fromLonLat } from 'ol/proj';
import { InterconnectTooltip } from '../interconnects/InterconnectTooltip';
import { MapTooltip } from './MapTooltip';
import { Interconnect, useGetInterconnectsQuery } from '../services/edgeApi';
import { MapPopup, MapPopupRef } from './MapPopup';
import { InterconnectPopup } from '../interconnects/InterconnectPopup';
import { useLocation } from 'react-router-dom';
import { colors } from '../utils/colors';
import { useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';
import { useMapContext, MapEventListener } from './context/MapContext';

const getInterconnectStyle = () => [
  {
    filter: ['>', ['get', 'enabled'], 0],
    style: {
      'stroke-width': 4,
      'stroke-color': colors.elements.interconnectColor,
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
    },
  },
  {
    filter: ['<=', ['get', 'enabled'], 0],
    style: {
      'stroke-width': 4,
      'stroke-color': colors.elements.disabledInterconnectColor,
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      'stroke-line-dash': [15, 10, 5, 10],
      'stroke-line-dash-offset': 0,
    },
  },
];

export type MapInterconnectsProps = {
  zIndex: number;
};

export const MapInterconnects: React.FC<MapInterconnectsProps> = ({
  zIndex,
}) => {
  const location = useLocation();
  useGetInterconnectsQuery(undefined);
  const allInterconnects = useSelector(selectInterconnects);
  const interconnects = useSelector(selectFilteredInterconnects);
  const {
    registerHoverListener,
    registerClickListener,
    unregisterHoverListener,
    unregisterClickListener,
  } = useMapContext();
  const nodes = useSelector(selectAllNodes);
  const allPoints = useSelector(selectAllPoints);
  const filteredTsps = useSelector(selectFilteredTsps);

  const [tooltipInterconnect, setTooltipInterconnect] =
    useState<Interconnect | null>(null);
  const [popupInterconnect, setPopupInterconnect] =
    useState<Interconnect | null>(null);

  const popupRef = useRef<MapPopupRef>(null);

  const isMapBuilderPage = useMemo(
    () => location.pathname.includes('/map-builder'),
    [location.pathname],
  );

  const hoverListener: MapEventListener = useCallback(
    (feature) => {
      if (
        feature?.getProperties()?.key ===
        `interconnect-${tooltipInterconnect?.id}`
      )
        return;
      if (
        !feature ||
        !feature?.getProperties()?.key?.startsWith('interconnect-')
      ) {
        setTooltipInterconnect(null);
        return;
      }
      const interconnectId = feature.getProperties()?.id;
      const interconnect = allInterconnects?.[interconnectId];
      setTooltipInterconnect(interconnect);
    },
    [allInterconnects, tooltipInterconnect, setTooltipInterconnect],
  );

  const clickListener: MapEventListener = useCallback(
    (feature, coords) => {
      if (
        !feature ||
        !feature?.getProperties()?.key?.startsWith('interconnect-')
      ) {
        setPopupInterconnect(null);
        popupRef.current?.hide();
        return;
      }
      const interconnectId = feature.getProperties()?.id;
      const interconnect = allInterconnects?.[interconnectId];
      setPopupInterconnect(interconnect);
      popupRef.current?.show(coords);
    },
    [allInterconnects, popupRef, setPopupInterconnect],
  );

  const closePopup = useCallback(() => {
    setPopupInterconnect(null);
    popupRef.current?.hide();
  }, [popupRef, setPopupInterconnect]);

  const features = useMemo(() => {
    return interconnects
      .map((interconnect) => {
        const startPoint = allPoints[interconnect.start_point];
        const startNode = nodes[startPoint?.node ?? ''];
        const endPoint = allPoints[interconnect.end_point];
        const endNode = nodes[endPoint?.node ?? ''];
        const enabled = interconnect.enabled;
        const positions = [
          [startNode?.lat, startNode?.lng],
          [endNode?.lat, endNode?.lng],
        ];

        return {
          interconnect,
          startNode,
          startPoint,
          endNode,
          endPoint,
          enabled,
          positions,
        };
      })
      .filter(({ startNode, endNode }) => {
        return startNode?.lat && startNode?.lng && endNode?.lat && endNode?.lng;
      })
      .map(({ startNode, endNode, interconnect }) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              fromLonLat([Number(startNode.lng), Number(startNode.lat)]),
              fromLonLat([Number(endNode.lng), Number(endNode.lat)]),
            ],
          },
          properties: {
            id: interconnect.id,
            key: `interconnect-${interconnect.id}`,
            enabled: interconnect.enabled ? 1 : 0,
          },
        } as FeatureLike;
      });
  }, [interconnects, nodes, allPoints, filteredTsps]);

  const style = useMemo(() => {
    return getInterconnectStyle();
  }, []);

  useMapLayer(features, { zIndex, style });

  useEffect(() => {
    registerHoverListener(hoverListener);
    if (isMapBuilderPage) {
      registerClickListener(clickListener);
    } else {
      popupRef.current?.hide();
    }
    return () => {
      unregisterHoverListener(hoverListener);
      if (isMapBuilderPage) {
        unregisterClickListener(clickListener);
      }
    };
  }, [
    isMapBuilderPage,
    clickListener,
    registerClickListener,
    unregisterClickListener,
    hoverListener,
    registerHoverListener,
    unregisterHoverListener,
  ]);

  return (
    <>
      <MapTooltip visible={Boolean(tooltipInterconnect)} maxWidth={400}>
        <div className="p-2 bg-dark border-dark text-light rounded">
          <InterconnectTooltip interconnect={tooltipInterconnect} />
        </div>
      </MapTooltip>
      <MapPopup ref={popupRef} onClose={closePopup}>
        <InterconnectPopup
          interconnectId={popupInterconnect?.id}
          closePopup={closePopup}
        />
      </MapPopup>
    </>
  );
};
